import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { NAV } from "../lib/site";
import { useSettings } from "../lib/hooks";
import { telLink, track } from "../lib/api";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { data: settings } = useSettings();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <>
      <header
        data-testid="site-navbar"
        className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color] duration-500 border-b ${
          scrolled ? "bg-graphite/95 backdrop-blur-md border-white/10" : "bg-transparent border-transparent"
        }`}
      >
        <div className="max-w-[1440px] mx-auto px-5 md:px-10 h-16 md:h-20 flex items-center justify-between">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 group shrink-0">
            <img
              src="/logo-nobg.webp"
              alt="NOOR Design & Build Ltd"
              className="h-12 md:h-16 w-auto transition-opacity duration-300 group-hover:opacity-85"
            />
          </Link>

          <nav className="hidden lg:flex items-center gap-7" aria-label="Primary">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `text-[13px] font-medium tracking-wide transition-colors duration-300 ${
                    isActive ? "text-ember" : "text-white/80 hover:text-white"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <a
              href={telLink(settings)}
              onClick={() => track("phone_click", { location: "navbar" })}
              data-testid="nav-call-button"
              className="hidden md:flex items-center gap-2 text-white/80 hover:text-white text-[13px] transition-colors duration-300"
              aria-label="Call NOOR Design & Build"
            >
              <Phone size={14} strokeWidth={1.5} />
              <span className="hidden xl:inline">{settings?.phone_display || "07782 760 279"}</span>
            </a>
            <Link
              to="/request-a-consultation"
              data-testid="nav-consultation-button"
              className="btn-sheen hidden md:inline-flex items-center bg-ember hover:bg-emberdark text-white text-[13px] font-semibold tracking-wide px-5 py-2.5 transition-colors duration-300"
            >
              Request a Consultation
            </Link>
            <button
              onClick={() => setOpen(!open)}
              data-testid="mobile-menu-toggle"
              className="lg:hidden text-ember p-2"
              aria-label={open ? "Close menu" : "Open menu"}
            >
              {open ? <X size={22} strokeWidth={2} /> : <Menu size={22} strokeWidth={2} />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 w-full max-w-[100vw] bg-graphite blueprint-grid flex flex-col px-8 pt-24 pb-28 overflow-y-auto overflow-x-clip [justify-content:safe_center] lg:hidden"
            data-testid="mobile-menu"
          >
            <div className="w-full min-w-0 max-w-full">
            <nav className="space-y-1" aria-label="Mobile">
              {NAV.map((item, i) => (
                <motion.div
                  key={item.to}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i, duration: 0.5 }}
                >
                  <NavLink
                    to={item.to}
                    data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className={({ isActive }) =>
                      `block py-2.5 font-display text-2xl sm:text-3xl font-light tracking-tight border-b border-white/10 ${
                        isActive ? "text-ember" : "text-white"
                      }`
                    }
                  >
                    {item.label}
                  </NavLink>
                </motion.div>
              ))}
            </nav>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.45 }}
              className="mt-8 mb-4"
            >
              <Link
                to="/request-a-consultation"
                data-testid="mobile-menu-consultation-button"
                className="block w-full box-border text-center bg-ember text-white font-semibold tracking-wide px-6 py-4"
              >
                Request a Consultation
              </Link>
            </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
